<template>
  <div
    class="sidebar-settings"
    data-test="graphics-sportradarSettings"
  >
    <div class="sidebar-settings-title sportradar-data-source-title">
      Data Source<br>
      <div class="league-label">{{leagueLabel}}</div>
    </div>

    <!-- Stuff for demo -->
    <div>
      <label
        v-if="controls.season"
        class="sidebar-form-label"
      >
        Season
        <span
          v-if="isLoading"
          class="far fa-spinner-third fa-spin"
          data-test="graphics-loader-settings-season"
        />
        <validation-field as="select"
          v-model="_seasonId"
          rules="required"
          data-test="dd-season"
          data-entity="graphics-selectSeasonSR"
          name="season"
          :disabled="isLoading"
        >
          <option
            :value="null"
            disabled
          >Select Season</option>
          <option
            v-for="s in seasons"
            :key="s.seasonId"
            :value="s.seasonId"
          >
            {{ s.seasonYear }} - {{ s.seasonTypeName }}
          </option>
        </validation-field>
      </label>

      <label
        v-if="filterByConference"
        class="sidebar-form-label"
      >
        Conference
        <span
          v-if="isLoading"
          class="far fa-spinner-third fa-spin"
          data-test="graphics-loader-settings-conference"
        />
        <validation-field as="select"
          v-model="_conferenceFilter"
          rules="required"
          :disabled="isLoading"
          data-test="dd-conference"
          data-entity="graphics-selectConferenceSR"
          name="conference"
        >
          <option
            :value="null"
            disabled
          >Select Conference</option>
          <option :value="'all'">All Conferences</option>
          <option
            v-for="c in conferences"
            :key="c.id"
            :value="c.name"
          >
            {{ c.name }}
          </option>
        </validation-field>
      </label>

      <label
        v-if="controls.team"
        class="sidebar-form-label"
      >
        Team
        <span
          v-if="isLoading"
          class="far fa-spinner-third fa-spin"
          data-test="graphics-loader-settings-team"
        />
        <validation-field as="select"
          v-model="_teamId"
          rules="required"
          :disabled="isLoading"
          data-test="dd-team"
          data-entity="graphics-selectTeamSR"
          name="team"
        >
          <option
            :value="null"
            disabled
          >Select Team</option>
          <option
            v-for="t in displayTeams"
            :key="t.teamId"
            :value="t.teamId"
          >
            {{ t.teamDisplayName }}
          </option>
        </validation-field>
      </label>

      <label
        v-if="controls.player"
        class="sidebar-form-label"
      >
        Player
        <span
          v-if="isLoading"
          class="far fa-spinner-third fa-spin"
          data-test="graphics-loader-settings-player"
        />
        <validation-field as="select"
          v-model="_playerId"
          rules="required"
          :disabled="isLoading"
          data-test="dd-player"
          data-entity="graphics-selectPlayerSR"
          name="player"
        >
          <option
            :value="null"
            disabled
          >Select Player</option>
          <option
            v-for="p in players"
            :key="p.playerId"
            :value="p.playerId"
          >
            {{ p.lastName }}, {{ p.firstName }}
          </option>
        </validation-field>
      </label>

      <label
        v-if="controls.game"
        class="sidebar-form-label"
      >
        Game
        <span
          v-if="isLoading"
          class="far fa-spinner-third fa-spin"
          data-test="graphics-loader-settings-game"
        />
        <validation-field as="select"
          v-model="_gameId"
          rules="required"
          :disabled="isLoading"
          data-test="dd-game"
          data-entity="graphics-selectGameSR"
          name="game"
        >
          <option
            :value="null"
            disabled
          >Select Game</option>
          <option
            v-for="g in teamGames"
            :key="g.gameId"
            :value="g.gameId"
          >
            {{ $filters.eventDisplayName(g) }}
          </option>
        </validation-field>
      </label>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import { mapState, mapActions } from 'vuex';
import leagueInfo from '../../../../utils/leagueInfo';

export default {
  name: 'SportradarSettings',
  props: ['element', 'template'],

  data() {
    return {
      error: null,
      syncing: false,
      loadingStack: [],
      // Make the timer reactive
      now: Date.now(),
    };
  },

  computed: {
    ...mapState({
      seasons(state) {
        return state[this.element.id].seasons;
      },
      Season(state) {
        return state[this.element.id].Season;
      },
      leagueName(state) {
        return state[this.element.id].leagueName;
      },
      teams(state) {
        return state[this.element.id].teams;
      },
      Team(state) {
        return state[this.element.id].Team;
      },
      statistics(state) {
        return state[this.element.id].statistics;
      },
      Player(state) {
        return state[this.element.id].Player;
      },
      players(state) {
        return state[this.element.id].players;
      },
      Game(state) {
        return state[this.element.id].Game;
      },
      games(state) {
        return state[this.element.id].games;
      },
      controls(state) {
        return state[this.element.id].controlOptions;
      },
      ConferenceFilter(state) {
        return state[this.element.id].conferenceFilter;
      },
      leagueLabel() {
        return leagueInfo[this.leagueName]?.label;
      },
      sortedTeamsByDisplayName() {
        const teams = Object.values(this?.teams || {});
        if (!teams?.length) return [];
        return teams?.sort((a, b) => {
          if (a.teamDisplayName < b.teamDisplayName) return -1;
          if (a.teamDisplayName > b.teamDisplayName) return 1;
          return 0;
        });
      },
    }),
    displayTeams() {
      if (!this.filterByConference || !this._conferenceFilter || this._conferenceFilter === 'all') {
        return this.sortedTeamsByDisplayName;
      }

      return this.sortedTeamsByDisplayName?.filter(
        (team) => team.Conference?.conferenceName === this._conferenceFilter,
      ) || [];
    },
    filterByConference() {
      return leagueInfo[this.leagueName]?.additionalFilters?.includes('conference');
    },
    conferences() {
      const teams = Object.values(this?.teams || {});
      if (!teams?.length || !this.filterByConference) {
        return [];
      }

      // Dedupe conferences
      const conferenceMap = new Map();
      teams.forEach((team) => {
        const conference = team?.Conference;
        if (conference && !conferenceMap.has(conference.conferenceId)) {
          conferenceMap.set(conference.conferenceId, {
            name: conference.conferenceName,
            id: conference.conferenceId,
          });
        }
      });

      return Array.from(conferenceMap.values()).sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    },
    _seasonId: {
      get() {
        return this.Season?.seasonId;
      },
      set(v) {
        const season = this.seasons[v];
        this.$store.commit(`${[this.element.id]}/SET_SEASON`, season);
        this.handleSeasonChange(v);
      },
    },
    _teamId: {
      get() {
        return this.Team?.teamId;
      },
      set(v) {
        this.$store.commit(`${[this.element.id]}/SET_TEAM`, this.teams[v]);
        this.handleTeamChange(v);
      },
    },
    _playerId: {
      get() {
        return this.Player?.playerId;
      },
      set(v) {
        const player = this.players.find((p) => p.playerId === v);
        this.$store.commit(`${[this.element.id]}/SET_PLAYER`, player);
        this.handlePlayerChange(v);
      },
    },
    _gameId: {
      get() {
        return this.Game?.gameId;
      },
      set(v) {
        const game = this.games.find((g) => g.gameId === v);
        this.$store.commit(`${[this.element.id]}/SET_GAME`, game);
        this.handleGameChange(v);
      },
    },
    _conferenceFilter: {
      get() {
        return this.ConferenceFilter || 'all';
      },
      set(v) {
        this.handleConferenceChange(v);
      },
    },
    isLoading() {
      return this.loadingStack.length > 0;
    },
    teamGames() {
      const isPlayersPool = leagueInfo[this.leagueName]?.participantType === 'players_pool';
      return (isPlayersPool)
        ? this.games
        : this.games?.filter((g) => [g.homeTeamId, g.awayTeamId]?.includes(this._teamId)); // eslint-disable-line no-underscore-dangle, max-len
    },
    isSetup() {
      return !Object.values(this.controls).some((bool) => bool);
    },

    /**
     * Get the amount of time since last sync and pretty it up.
     * Can probably be broken out to a shared util if needed elsewhere.
     */
    lastSyncLabel() {
      const { lastSync } = this.element.data;
      if (!lastSync) {
        return '';
      }

      const delta = (this.now - lastSync) / 1e3;
      let seconds = Math.floor(delta);

      if (seconds <= 0) {
        return 'less than a minute ago';
      }

      let minutes = Math.floor(seconds / 60);
      seconds -= minutes * 60;

      let hours = Math.floor(minutes / 60);
      minutes -= hours * 60;

      const days = Math.floor(hours / 24);
      hours -= days * 24;

      if (days > 7) {
        // Just show the date
        // TODO: Format prettier
        return new Date(lastSync).toISOString();
      }

      if (days >= 1) {
        return `${days} day${days > 1 ? 's' : ''} ago`;
      }

      if (hours >= 1) {
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }

      if (minutes >= 1) {
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      }

      return 'less than a minute ago';
    },
  },

  async mounted() {
    this.loadingStack.push(1);
    const statType = this.controls.player ? 'player' : 'team';
    const statPeriod = this.controls.game ? 'game' : 'season';
    await this.getStatSelectionData({ statPeriod, statType });
    this.loadingStack.pop();
    // We don't need a fancy timer so we don't need a 1s ticker
    this.interval = setInterval(() => {
      this.now = Date.now();
    }, 5e3);
  },

  beforeUnmount() {
    clearInterval(this.interval);
  },

  methods: {
    ...mapActions({
      syncGqlDataFromElement(dispatch, payload) {
        return dispatch(`${this.element.id}/syncGqlDataFromElement`, payload);
      },
      statSelectorChange(dispatch, payload) {
        return dispatch(`${this.element.id}/statSelectorChange`, payload);
      },
      getStatSelectionData(dispatch, payload) {
        return dispatch(`${this.element.id}/getStatSelectionData`, payload);
      },
    }),
    async _statSelectorChange(selectionType) {
      try {
        await this.statSelectorChange({
          leagueId: this.Season.leagueId,
          teamId: this._teamId,
          seasonId: this._seasonId,
          statPeriod: this.controls.game ? 'game' : 'season',
          statType: this.controls.player ? 'player' : 'team',
          conferenceFilter: this._conferenceFilter || null,
          selectionType,
        });
      } catch (error) {
        console.error('Error getting team season modal data', error);
      }
    },
    async handlePlayerChange(playerId) {
      this.loadingStack.push(1);
      try {
        await this.syncGqlDataFromElement({
          playerId,
          teamId: this._teamId,
          seasonId: this._seasonId,
          gameId: this._gameId,
          statPeriod: this.controls.game ? 'game' : 'season',
          statType: this.controls.player ? 'player' : 'team',
          leagueName: this.leagueName,
        });
      } catch (error) {
        console.error('Error getting player data', error);
      }
      this.loadingStack.pop();
    },
    async handleGameChange(gameId) {
      this.loadingStack.push(1);
      try {
        await this.syncGqlDataFromElement({
          playerId: this._playerId,
          teamId: this._teamId,
          seasonId: this._seasonId,
          gameId,
          statPeriod: this.controls.game ? 'game' : 'season',
          statType: this.controls.player ? 'player' : 'team',
          leagueName: this.leagueName,
        });
      } catch (error) {
        console.error('Error getting player data', error);
      }
      this.loadingStack.pop();
    },
    async handleSeasonChange(seasonId) {
      this.loadingStack.push(1);
      try {
        let teamId = this._teamId;
        if (!teamId || teamId === 'free_agent') {
          // If no team is selected or the selected team is 'free_agent' choose the first team.
          const firstTeamInList = Object.values(this.teams)[0];
          teamId = firstTeamInList.teamId;
        }

        await this._statSelectorChange('season');

        await this.syncGqlDataFromElement({
          teamId,
          playerId: this._playerId,
          gameId: this._gameId,
          seasonId,
          statPeriod: this.controls.game ? 'game' : 'season',
          statType: this.controls.player ? 'player' : 'team',
          leagueName: this.leagueName,
        });
        // check if new teamId is still in the list of teams, if not, reset to first team
        // this edgecase shouldn't happen if data is okay.
        if (!Object.values(this.displayTeams).some((t) => t.teamId === teamId)) {
          this._teamId = this.teams[0]?.teamId || null;
        }
      } catch (error) {
        console.error('Error getting player and team data', error);
      }

      this.loadingStack.pop();
    },

    async handleTeamChange(teamId) {
      this.loadingStack.push(1);
      try {
        await this._statSelectorChange('team');
        await this.syncGqlDataFromElement({
          playerId: this._playerId,
          gameId: this._gameId,
          teamId,
          seasonId: this._seasonId,
          statPeriod: this.controls.game ? 'game' : 'season',
          statType: this.controls.player ? 'player' : 'team',
          leagueName: this.leagueName,
        });
      } catch (error) {
        console.error('Error getting player and team data', error);
      }

      if (this._playerId && !Object.values(this.players).some((p) => p.playerId === this._playerId)) {
        // selected player wasn't on this team
        this.Player = { playerId: null };
      }
      this.loadingStack.pop();
    },
    async handleConferenceChange(v) {
      if (this._conferenceFilter === v) return; // Avoid unnecessary updates
      this.$store.commit(`${this.element.id}/SET_CONFERENCE_FILTER`, v);
      await this._statSelectorChange('conference');
      if (!v || v === 'all') {
        return;
      }

      const conferenceTeams = this.sortedTeamsByDisplayName.filter(
        (team) => team.Conference?.conferenceName === v,
      );
      this._teamId = conferenceTeams[0]?.teamId || null;
    },
  },
};
</script>
